// The daily digest — everything the pipeline captured for one issue
// date, which in Phase 0 is the whole product. You read this and write
// the newsletter from it.
//
// Two things this page has to do, learned the hard way:
//
//   1. Lead with recency. The first live run captured a site's
//      navigation skeleton and reported it as a good day, because
//      nothing on the page distinguished "published this morning" from
//      "last touched in 2016". Every item now shows when the SOURCE
//      published it, and the list is ordered by that.
//
//   2. Be explorable. A digest you can't click into is a list of
//      assertions. Every document, every town, every troubled source
//      opens into the thing behind it — the captured text, the URL, the
//      fetch time, the R2 key.
//
// Still deliberately not ranked or summarized. Phase 0 exists to learn
// what surfaces in Bangor on a slow Tuesday, and pre-sorting by a guess
// at importance would hide exactly that.

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileSearch,
  MapPin,
  RefreshCw,
  Rss,
  Search,
} from 'lucide-react';
import { pipelineApi, sourcesApi, ApiError } from '../lib/api';
import SnapshotDrawer from './SnapshotDrawer';
import type { Digest, DigestDocument, DigestSource, TownCoverage } from '@shared/types';

function shiftDate(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function fmtTime(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function fmtDateLong(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function relative(ms: number | null): string {
  if (ms === null) return 'undated';
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export default function DigestPage() {
  const { date } = useParams<{ date?: string }>();
  const navigate = useNavigate();
  const [digest, setDigest] = useState<Digest | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [openSnapshot, setOpenSnapshot] = useState<string | null>(null);

  const target = date ?? 'today';

  const load = useCallback(async () => {
    try {
      setErr(null);
      const { digest } = await pipelineApi.digest(target);
      setDigest(digest);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load digest');
    }
  }, [target]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runNow() {
    if (!confirm('Run retrieval now? Discovery is capped, so this fetches at most ~16 items.'))
      return;
    setBusy(true);
    try {
      await sourcesApi.runRetrieval();
      // The endpoint returns 202 and the work continues in the
      // background, so give it a moment before re-reading.
      setTimeout(() => void load(), 8000);
    } catch (e) {
      alert(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed');
    } finally {
      setBusy(false);
    }
  }

  const iso = digest?.issue_date ?? null;
  const allDocs = (digest?.sources ?? [])
    .flatMap((s) => s.documents.map((d) => ({ doc: d, source: s })))
    .sort((a, b) => (b.doc.published_at ?? 0) - (a.doc.published_at ?? 0));

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="max-w-5xl mx-auto py-6 px-4 sm:py-10 sm:px-6">
        <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <FileSearch size={18} className="text-slate-600" />
              <h1 className="font-display text-2xl sm:text-3xl text-slate-900">Digest</h1>
            </div>
            {iso && (
              <p className="text-sm text-slate-500 mt-1">
                Captured for the issue of {fmtDateLong(iso)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {iso && (
              <>
                <button
                  className="btn-ghost"
                  onClick={() => navigate(`/digest/${shiftDate(iso, -1)}`)}
                  title="Previous day"
                >
                  <ChevronLeft size={16} />
                </button>
                <button className="btn-secondary" onClick={() => navigate('/digest')}>
                  Today
                </button>
                <button
                  className="btn-ghost"
                  onClick={() => navigate(`/digest/${shiftDate(iso, 1)}`)}
                  title="Next day"
                >
                  <ChevronRight size={16} />
                </button>
              </>
            )}
            <button className="btn-primary" onClick={runNow} disabled={busy}>
              <RefreshCw size={14} className={busy ? 'animate-spin' : ''} />
              {busy ? 'Running…' : 'Run now'}
            </button>
          </div>
        </header>

        {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

        {digest === null ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : (
          <>
            <RunSummary digest={digest} />
            <Coverage digest={digest} onOpen={setOpenSnapshot} />
            <Attention items={digest.attention} />
            <Captured docs={allDocs} onOpen={setOpenSnapshot} />
          </>
        )}
      </div>

      {openSnapshot && (
        <SnapshotDrawer snapshotId={openSnapshot} onClose={() => setOpenSnapshot(null)} />
      )}
    </div>
  );
}

function RunSummary({ digest }: { digest: Digest }) {
  const run = digest.run;
  if (!run) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-6">
        <p className="text-sm text-slate-600">
          No retrieval run for this date yet. Retrieval fires at 03:00 UTC (11pm Eastern the
          evening before), or you can run it now.
        </p>
      </div>
    );
  }

  const tone =
    run.status === 'ok'
      ? 'bg-green-100 text-green-800'
      : run.status === 'partial'
        ? 'bg-amber-100 text-amber-800'
        : run.status === 'failed'
          ? 'bg-red-100 text-red-800'
          : 'bg-slate-200 text-slate-700';

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-6">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span
          className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${tone}`}
        >
          {run.status}
        </span>
        <span className="text-xs text-slate-500">
          {fmtTime(run.started_at)}
          {run.finished_at
            ? ` — ${fmtTime(run.finished_at)} (${Math.round((run.finished_at - run.started_at) / 1000)}s)`
            : ' — still running'}
        </span>
      </div>
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Documents" value={digest.totals.docs} />
        <Stat label="New today" value={digest.totals.new_docs} accent />
        <Stat label="Sources reporting" value={digest.totals.sources_reporting} />
        <Stat label="Browser seconds" value={run.browser_seconds.toFixed(1)} />
      </dl>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={`text-lg font-medium tabular-nums ${accent ? 'text-blue-700' : 'text-slate-900'}`}>
        {value}
      </dd>
    </div>
  );
}

/**
 * Per-town capture, expandable into the sources behind each row.
 *
 * Hampden, Holden and Old Town block every retrieval path we have, so
 * their absence is a standing fact about the product rather than an
 * oversight — and clicking a town should show you exactly which
 * sources are off and why, not leave you guessing.
 */
function Coverage({
  digest,
  onOpen,
}: {
  digest: Digest;
  onOpen: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <section className="mb-6">
      <h2 className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-1.5">
        <MapPin size={14} className="text-slate-500" />
        Coverage by town
      </h2>
      <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
        {digest.town_coverage.map((t: TownCoverage) => {
          const blocked = t.sources_enabled === 0 && t.sources_total > 0;
          const quiet = t.sources_enabled > 0 && t.docs === 0;
          const isOpen = expanded === t.town;
          const townSources = digest.sources.filter((s) => s.town === t.town);

          return (
            <div key={t.town}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : t.town)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 text-left"
              >
                <ChevronDown
                  size={14}
                  className={`text-slate-400 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                />
                <span className={`text-sm flex-1 ${blocked ? 'text-slate-400' : 'text-slate-900'}`}>
                  {t.town}
                </span>
                {blocked && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                    no reachable sources
                  </span>
                )}
                {quiet && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">
                    nothing today
                  </span>
                )}
                <span className="text-xs text-slate-500 tabular-nums w-32 text-right">
                  {t.sources_enabled}/{t.sources_total} sources · {t.docs} docs
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-3 bg-slate-50/60">
                  {townSources.length === 0 ? (
                    <p className="text-xs text-slate-500 py-2">
                      No registry sources are assigned to {t.town}.
                    </p>
                  ) : (
                    <ul className="space-y-1.5 pt-1">
                      {townSources.map((s) => (
                        <SourceLine key={s.source_id} source={s} onOpen={onOpen} />
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SourceLine({
  source,
  onOpen,
}: {
  source: DigestSource;
  onOpen: (id: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <li className="text-xs">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          className="text-slate-800 hover:text-blue-700 hover:underline"
          onClick={() => navigate(`/settings/sources/${source.source_id}`)}
        >
          {source.name}
        </button>
        {!source.enabled && (
          <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-200 text-slate-600 px-1.5 rounded">
            off
          </span>
        )}
        <span className="text-slate-400">
          {source.docs} doc{source.docs === 1 ? '' : 's'}
        </span>
      </div>
      {source.documents.length > 0 && (
        <ul className="mt-1 ml-3 space-y-0.5">
          {source.documents.slice(0, 5).map((d) => (
            <li key={d.snapshot_id}>
              <button
                className="text-left text-slate-600 hover:text-blue-700 hover:underline"
                onClick={() => onOpen(d.snapshot_id)}
              >
                {d.title || d.url}
              </button>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function Attention({ items }: { items: Digest['attention'] }) {
  const navigate = useNavigate();
  if (items.length === 0) return null;

  return (
    <section className="mb-6">
      <h2 className="text-sm font-medium text-slate-900 mb-2 flex items-center gap-1.5">
        <AlertTriangle size={14} className="text-amber-600" />
        Needs attention
      </h2>
      <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
        {items.map((a) => (
          <li key={a.slug}>
            <button
              type="button"
              onClick={() => navigate(`/settings/sources/${a.source_id}`)}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-900">{a.name}</span>
                <span
                  className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                    a.alert_state === 'failing'
                      ? 'bg-red-100 text-red-800'
                      : a.alert_state === 'blocked'
                        ? 'bg-slate-200 text-slate-700'
                        : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {a.alert_state}
                </span>
                {a.failure_streak > 0 && (
                  <span className="text-xs text-slate-500">
                    {a.failure_streak} consecutive failure{a.failure_streak === 1 ? '' : 's'}
                  </span>
                )}
                <span className="flex-1" />
                <span className="text-xs text-blue-700">Open source →</span>
              </div>
              {a.last_error && (
                <p className="text-xs text-slate-500 mt-0.5 font-mono truncate">{a.last_error}</p>
              )}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Everything captured, newest published first.
 *
 * One flat list rather than grouped by source: what an editor wants at
 * 6am is "what happened", not "what did the Chamber's website do".
 */
function Captured({
  docs,
  onOpen,
}: {
  docs: Array<{ doc: DigestDocument; source: DigestSource }>;
  onOpen: (id: string) => void;
}) {
  if (docs.length === 0) {
    return (
      <section>
        <h2 className="text-sm font-medium text-slate-900 mb-2">Captured</h2>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <p className="text-sm text-slate-500">
            Nothing captured for this date. If a run completed, check the permission verdicts on
            the Sources page — an unaudited source is skipped rather than fetched.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <h2 className="text-sm font-medium text-slate-900 mb-2">
        Captured <span className="text-slate-400 font-normal">({docs.length})</span>
      </h2>
      <ul className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
        {docs.map(({ doc, source }) => (
          <li key={doc.snapshot_id}>
            <button
              type="button"
              onClick={() => onOpen(doc.snapshot_id)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 group"
            >
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-slate-900 group-hover:text-blue-700">
                    {doc.title || doc.url}
                  </span>
                  <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                    <span className="text-slate-600">{source.name}</span>
                    <span className="text-slate-300">·</span>
                    <span
                      className={doc.published_at === null ? 'text-amber-700' : 'text-slate-500'}
                    >
                      {relative(doc.published_at)}
                    </span>
                    <span className="text-slate-300">·</span>
                    <span className="inline-flex items-center gap-1 text-slate-400">
                      {doc.lane === 'search' ? <Search size={10} /> : <Rss size={10} />}
                      {doc.lane ?? 'feed'}
                    </span>
                    {doc.is_new && (
                      <span className="text-[10px] uppercase tracking-wide font-semibold bg-blue-100 text-blue-800 px-1.5 rounded">
                        new
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">{doc.url}</p>
                </div>
                <ExternalLink
                  size={12}
                  className="mt-1 text-slate-300 group-hover:text-blue-700 flex-shrink-0"
                />
              </div>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-xs text-slate-400 mt-2">
        Click any item to see the captured text, where it was scraped from, and where it is stored.
      </p>
    </section>
  );
}
